import { query } from "@/api/app-search/app-search";
import BookingModel from "@/app-search/models/BookingModel";
import {
  EngineKeys,
  EngineTypes,
} from "@/app-search/services/request-app-search.service";
import NotificationService from "@/services/notification.service";
import {
  getModule,
  VuexModule,
  Mutation,
  Action,
  Module,
} from "vuex-module-decorators";
import store from "..";
import {
  IDateOfVisitFilter,
  IOrderState,
  IOrderTableColumn,
  OrderTableColumns,
} from "@/interfaces/store/IOrder";
import { SortingDirections, SortingOptions } from "@/app-search/enums/enums";

@Module({ dynamic: true, namespaced: true, store, name: "Order" })
class OrderStoreModule extends VuexModule implements IOrderState {
  public filters: any = {};
  public orders: any = {};
  public ticketReferences: string[] = [];

  public orderTableConfig: IOrderTableColumn[] = [
    {
      id: OrderTableColumns.REFERENCE,
      label: "orders.booking-reference",
      enabled: true,
    },
    {
      id: OrderTableColumns.CUSTOMER,
      label: "orders.customer-full-name",
      enabled: true,
    },
    {
      id: OrderTableColumns.DATE_OF_VISIT,
      label: "filters.date-of-visit",
      enabled: true,
    },
    {
      id: OrderTableColumns.QUANTITY,
      label: "orders.quantity",
      enabled: true,
    },
    {
      id: OrderTableColumns.TICKETS,
      label: "completeorder.tickets",
      enabled: true,
    },
    {
      id: OrderTableColumns.RETAILS,
      label: "orders.retail-product",
      enabled: true,
    },
    {
      id: OrderTableColumns.ADDONS,
      label: "orders.addons",
      enabled: true,
    },
    {
      id: OrderTableColumns.CREATION_DATE,
      label: "orders.creation-date",
      enabled: true,
    },
    {
      id: OrderTableColumns.PAYMENT_STATUS,
      label: "orders.payment-status",
      enabled: true,
    },
    {
      id: OrderTableColumns.SELLER,
      label: "orders.seller",
      enabled: true,
    },
    {
      id: OrderTableColumns.CHANNEL,
      label: "orders.booking-channel",
      enabled: true,
    },
    {
      id: OrderTableColumns.TOTAL_VALUE,
      label: "orders.total-price",
      enabled: true,
    },
    {
      id: OrderTableColumns.TEAMS,
      label: "completeorder.teams",
      enabled: false,
    },
  ];

  // date-of-visit filter
  public dateOfVisitFilter: IDateOfVisitFilter | null = null;
  public selectedSortingOption = SortingOptions.CreatedAt;
  public selectedSortingDirection = SortingDirections.Descending;

  /**
   * Returns an array of parsed orders
   */
  // eslint-disable-next-line
  public get getParsedOrders(): any[] {
    if (!this.orders.booking) {
      return [];
    }
    return this.orders.booking.results.map(
      // eslint-disable-next-line
      (item: any) => new BookingModel(item)
    );
  }

  public get ordersCount() {
    if (!this.orders.booking) {
      return 0;
    }
    return this.orders.booking.info.meta.page.total_results || 0;
  }

  /**
   * Fetch bookings list
   *
   * @param {object} payload: params for request
   * @returns {Promise<{ customers: object }>}
   */
  @Action({ rawError: true })
  // eslint-disable-next-line
  public async fetchBooking(payload: any) {
    // eslint-disable-next-line no-useless-catch
    try {
      const booking = await query(
        EngineTypes.BookingList,
        EngineKeys.Booking,
        payload
      );

      if (booking) {
        this.SET_ORDERS({ booking });
      }
    } catch (err) {
      throw err;
    }
  }

  /**
   * Fetch tickets list
   * @param {object} payload: params for request
   */
  @Action
  // eslint-disable-next-line
  public async fetchTickets(payload: any) {
    // eslint-disable-next-line no-useless-catch
    try {
      const res = await query(
        EngineTypes.TicketList,
        EngineKeys.Ticket,
        payload
      );
      if (res) {
        const refsList = res.results.map((ticket: any) => {
          const ref = ticket.data.ticket_booking_reference;
          if (ref) return ref.raw;
        });
        this.setTickets(refsList);
      }
    } catch (err) {
      throw err;
    }
  }

  @Mutation
  // eslint-disable-next-line
  private SET_ORDERS(payload: any) {
    this.orders = payload;
  }

  @Mutation
  // eslint-disable-next-line
  public setTickets(payload: any) {
    this.ticketReferences = payload;
  }

  @Mutation
  // eslint-disable-next-line
  public setTableConfig(payload: IOrderTableColumn[]) {
    this.orderTableConfig = payload;
  }

  @Mutation
  public setDateOfVisitFilter(payload: IDateOfVisitFilter | null) {
    this.dateOfVisitFilter = payload;
  }

  @Mutation
  public setTicketReferences(payload: string[]) {
    this.ticketReferences = payload;
  }

  @Mutation
  public setSelectedSortingOption(payload: SortingOptions) {
    this.selectedSortingOption = payload;
  }

  @Mutation
  public setSelectedSortingDirection(payload: SortingDirections) {
    this.selectedSortingDirection = payload;
  }

  @Mutation
  public restore(moduleFields: IOrderState) {
    const {
      orderTableConfig,
      dateOfVisitFilter,
      selectedSortingOption,
      selectedSortingDirection,
    } = moduleFields;
    if (orderTableConfig) {
      this.orderTableConfig = orderTableConfig;
    }
    if (dateOfVisitFilter) {
      this.dateOfVisitFilter = dateOfVisitFilter;
    }
    if (selectedSortingOption) {
      this.selectedSortingOption = selectedSortingOption;
    }
    if (selectedSortingDirection) {
      this.selectedSortingDirection = selectedSortingDirection;
    }
  }
}
const OrderModule = getModule(OrderStoreModule);
export default OrderModule;
